import { Node, Edge } from 'reactflow';

/**
 * Cached workflow graph structure
 * Only recalculated when nodes/edges structure changes (not on position updates)
 */
interface WorkflowGraphCache {
  workflowId: string;
  structureHash: string; // Hash of node IDs and edge connections
  adjacencyList: Map<string, Set<string>>; // nodeId → Set of predecessor IDs
  nodeMap: Map<string, Node>; // Quick node lookup
  loopParents: Map<string, string>; // nodeId → parent loop ID
  loopChildren: Map<string, Set<string>>; // loopId → Set of child node IDs
  calculatedAt: number;
}

/**
 * Result of predecessor lookup with metadata
 */
export interface PredecessorInfo {
  nodeId: string;
  node: Node;
  distance: number;
  parentLoopId?: string;
  sourceHandle?: string;
}

class WorkflowGraphService {
  private cache: WorkflowGraphCache | null = null;

  /**
   * Calculate a structural hash of the workflow
   * Only includes node IDs and connections (not positions, labels, etc.)
   */
  private calculateStructureHash(nodes: Node[], edges: Edge[]): string {
    const nodeIds = nodes.map(n => n.id).sort().join(',');
    const edgeConnections = edges
      .map(e => `${e.source}:${e.sourceHandle || 'default'}->${e.target}`)
      .sort()
      .join(',');
    return `${nodeIds}|${edgeConnections}`;
  }

  /**
   * Build adjacency list for efficient predecessor lookups
   */
  private buildAdjacencyList(edges: Edge[]): Map<string, Set<string>> {
    const adj = new Map<string, Set<string>>();
    
    for (const edge of edges) {
      if (!adj.has(edge.target)) {
        adj.set(edge.target, new Set());
      }
      adj.get(edge.target)!.add(edge.source);
    }
    
    return adj;
  }

  /**
   * Check if a node is a loop node
   */
  private isLoopNode(node: Node): boolean {
    return node?.type?.includes('loop') || 
           node?.data?.category === 'loop' || 
           node?.data?.isLoop === true;
  }

  /**
   * Check if a node is a loop aggregator
   */
  private isLoopAggregator(node: Node): boolean {
    return node?.type?.includes('aggregator') || 
           node?.data?.isLoopAggregator === true;
  }

  /**
   * Find all loop parent relationships and children
   */
  private findLoopRelationships(
    nodes: Node[], 
    edges: Edge[]
  ): { 
    loopParents: Map<string, string>; 
    loopChildren: Map<string, Set<string>> 
  } {
    const loopParents = new Map<string, string>();
    const loopChildren = new Map<string, Set<string>>();
    const loopNodes = nodes.filter(n => this.isLoopNode(n));
    
    for (const loopNode of loopNodes) {
      const loopId = loopNode.id;
      const childNodes = new Set<string>();
      
      // Find all nodes inside this loop by traversing from loop output
      const loopOutputEdges = edges.filter(e => 
        e.source === loopId && e.sourceHandle !== 'done'
      );
      
      const visited = new Set<string>();
      const traverse = (nodeId: string) => {
        if (visited.has(nodeId)) return;
        visited.add(nodeId);
        
        childNodes.add(nodeId);
        loopParents.set(nodeId, loopId);
        
        // Find outgoing edges (but don't go back to loop node)
        const outgoing = edges.filter(
          e => e.source === nodeId && e.target !== loopId
        );
        
        outgoing.forEach(e => traverse(e.target));
      };
      
      // Start traversal from first node(s) after loop output
      loopOutputEdges.forEach(e => traverse(e.target));
      
      loopChildren.set(loopId, childNodes);
    }
    
    return { loopParents, loopChildren };
  }

  /**
   * Get or build cached workflow graph
   */
  getWorkflowGraph(workflowId: string, nodes: Node[], edges: Edge[]): WorkflowGraphCache {
    const structureHash = this.calculateStructureHash(nodes, edges);
    
    // Return cached version if structure hasn't changed
    if (this.cache && 
        this.cache.workflowId === workflowId &&
        this.cache.structureHash === structureHash) {
      if (process.env.NODE_ENV === 'development') {
      }
      return this.cache;
    }
    
    if (process.env.NODE_ENV === 'development') {
    }
    
    const { loopParents, loopChildren } = this.findLoopRelationships(nodes, edges);
    
    // Build new cache
    this.cache = {
      workflowId,
      structureHash,
      adjacencyList: this.buildAdjacencyList(edges),
      nodeMap: new Map(nodes.map(n => [n.id, n])),
      loopParents,
      loopChildren,
      calculatedAt: Date.now(),
    };
    
    return this.cache;
  }

  /**
   * Check if an edge is a loop-back edge (from a node inside a loop back to the loop node)
   */
  private isLoopBackEdge(edge: Edge, graph: WorkflowGraphCache): boolean {
    const targetNode = graph.nodeMap.get(edge.target);
    if (!targetNode || !this.isLoopNode(targetNode)) return false;
    
    // Check if the source node is a child of the target loop node
    const loopChildren = graph.loopChildren.get(edge.target);
    if (!loopChildren) return false;
    
    // If source is a direct child of the loop, it's a loop-back edge
    if (loopChildren.has(edge.source)) return true;
    
    // Check if source is a descendant of any loop child (nested case)
    const isDescendant = (nodeId: string, ancestorId: string): boolean => {
      if (nodeId === ancestorId) return true;
      const parent = graph.loopParents.get(nodeId);
      if (!parent) return false;
      return isDescendant(parent, ancestorId);
    };
    
    return Array.from(loopChildren).some(childId => isDescendant(edge.source, childId));
  }

  /**
   * Get all predecessor nodes with metadata (efficient lookup)
   * This replaces the expensive getAllPreviousNodes recursive function
   * Excludes loop-back edges to prevent including downstream nodes
   */
  getPredecessorsWithMetadata(
    nodeId: string,
    graph: WorkflowGraphCache,
    edges: Edge[]
  ): PredecessorInfo[] {
    const result: PredecessorInfo[] = [];
    const visited = new Set<string>();
    
    // BFS traversal with distance tracking
    const queue: Array<{ id: string; distance: number }> = [{ id: nodeId, distance: 0 }];
    
    while (queue.length > 0) {
      const { id, distance } = queue.shift()!;
      
      if (visited.has(id)) continue;
      visited.add(id);
      
      // Get incoming edges for this node, excluding loop-back edges
      const incomingEdges = edges.filter(e => {
        if (e.target !== id) return false;
        // Skip loop-back edges
        if (this.isLoopBackEdge(e, graph)) {
          return false;
        }
        return true;
      });
      
      for (const edge of incomingEdges) {
        const predNode = graph.nodeMap.get(edge.source);
        if (!predNode) continue;
        
        const parentLoopId = graph.loopParents.get(edge.source);
        
        // Handle loop nodes specially
        if (this.isLoopNode(predNode) && edge.sourceHandle?.includes('done')) {
          // When connected to done handle, ONLY add the done output
          result.push({
            nodeId: predNode.id,
            node: predNode,
            distance: distance + 1,
            parentLoopId,
            sourceHandle: 'done',
          });
          
          // Add all nodes inside the loop (they executed during the loop)
          const loopChildren = graph.loopChildren.get(predNode.id);
          if (loopChildren) {
            for (const childId of loopChildren) {
              const childNode = graph.nodeMap.get(childId);
              if (childNode) {
                result.push({
                  nodeId: childId,
                  node: childNode,
                  distance: distance + 2,
                  parentLoopId: predNode.id,
                  sourceHandle: this.isLoopAggregator(childNode) ? 'aggregated' : undefined,
                });
              }
            }
          }
          
          // Continue traversing back from loop node to find upstream nodes
          queue.push({ id: predNode.id, distance: distance + 1 });
          continue;
        }
        
        // Normal node - add to result and continue traversal
        result.push({
          nodeId: predNode.id,
          node: predNode,
          distance: distance + 1,
          parentLoopId,
          sourceHandle: edge.sourceHandle || undefined,
        });
        
        // Add to queue for further traversal
        queue.push({ id: predNode.id, distance: distance + 1 });
      }
    }
    
    return result;
  }

  /**
   * Get input data for a node (optimized version of the inputData useMemo logic)
   * Note: Pass fresh nodes array to get current execution outputs
   */
  getInputDataForNode(
    nodeId: string,
    graph: WorkflowGraphCache,
    edges: Edge[],
    freshNodes?: Node[] // Optional fresh nodes array for current execution outputs
  ): Array<{
    nodeId: string;
    nodeName: string;
    nodeType?: string;
    nodeDistance: number;
    data: unknown;
    sourceHandle?: string;
  }> {
    const predecessors = this.getPredecessorsWithMetadata(nodeId, graph, edges);
    
    // Create a map of fresh node data if provided
    const freshNodeMap = freshNodes 
      ? new Map(freshNodes.map(n => [n.id, n]))
      : null;
    
    // Filter to only nodes with execution output AND exclude sub-nodes
    const nodesWithData = predecessors
      .filter(pred => {
        // Use fresh node data if available, otherwise use cached
        const node = freshNodeMap?.get(pred.nodeId) || pred.node;
        
        // Exclude nodes without execution output
        if (!node?.data?.executionOutput || pred.nodeId === nodeId) {
          return false;
        }
        
        // Exclude sub-nodes (nodes connected via subNodeInput target handle)
        const isSubNode = edges.some(edge => 
          edge.target === pred.nodeId && edge.targetHandle === 'subNodeInput'
        );
        
        return !isSubNode;
      })
      .map(pred => {
        // Use fresh node data if available, otherwise use cached
        const node = freshNodeMap?.get(pred.nodeId) || pred.node;
        
        // CRITICAL: ALWAYS return the FULL unified format
        // The unified format is: { success: boolean, output: any, logs: any[], metrics: any, _handles?: any }
        // DO NOT extract parts - the input panel should show the complete data structure
        let outputData = node.data.executionOutput;
        
        // If sourceHandle is specified (e.g., 'done'), extract data from _handles
        if (pred.sourceHandle && outputData?._handles?.[pred.sourceHandle]) {
          // Wrap the handle data in unified format if it's not already wrapped
          const handleData = outputData._handles[pred.sourceHandle];
          if (handleData && typeof handleData === 'object' && !('success' in handleData)) {
            outputData = {
              success: true,
              output: handleData,
              logs: outputData.logs || [],
              metrics: outputData.metrics || { duration: 0, tier: 'sandboxed' }
            };
          } else {
            outputData = handleData;
          }
        } else if (pred.sourceHandle) {
        }
        
        return {
          nodeId: pred.nodeId,
          nodeName: node.data.label || node.type,
          nodeType: node.type,
          nodeDistance: pred.distance,
          data: outputData, // Always the full unified format
          sourceHandle: pred.sourceHandle,
        };
      });
    
    // Deduplicate by nodeId + sourceHandle combination - keep only the occurrence with smallest distance
    // This allows multiple entries for the same node with different handles (e.g., Loop :data and Loop :done)
    const nodeMap = new Map<string, typeof nodesWithData[0]>();
    
    // console.log('[WorkflowGraphService] Before deduplication:', nodesWithData.map(n => ({ 
    //   nodeId: n.nodeId, 
    //   nodeName: n.nodeName, 
    //   sourceHandle: n.sourceHandle,
    //   key: n.sourceHandle ? `${n.nodeId}:${n.sourceHandle}` : n.nodeId
    // })));
    
    for (const item of nodesWithData) {
      const key = item.sourceHandle ? `${item.nodeId}:${item.sourceHandle}` : item.nodeId;
      const existing = nodeMap.get(key);
      if (!existing || item.nodeDistance < existing.nodeDistance) {
        nodeMap.set(key, item);
      }
    }
    
    const result = Array.from(nodeMap.values());
    // console.log('[WorkflowGraphService] After deduplication:', result.map(n => ({ 
    //   nodeId: n.nodeId, 
    //   nodeName: n.nodeName, 
    //   sourceHandle: n.sourceHandle 
    // })));
    
    return result;
  }

  /**
   * Clear cache (call when switching workflows)
   */
  clearCache() {
    this.cache = null;
  }
}

// Singleton instance
export const workflowGraphService = new WorkflowGraphService();
